import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { CardElement, injectStripe } from "react-stripe-elements";
import { moneyLabel } from "./payloadDisplay";
import {
  chargeOrder,
  createCheckout,
  createOrder,
  fetchCheckout,
  updateCheckoutBilling,
  updateCheckoutShipping,
  validateCheckout,
} from "./checkoutApi";
import "./CheckoutPage.css";

/**
 * A-009 — Checkout UI renders only checkoutPayload / orderPayload fields.
 * Payment: Stripe card token → commerce/order/charge (server total authoritative).
 */
function emptyAddress() {
  return {
    name: "",
    email: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    region: "",
    postal_code: "",
    country: "",
  };
}

function CheckoutPage(props) {
  const history = props.history;
  const stripe = props.stripe;
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [billing, setBilling] = useState(emptyAddress());
  const [shipping, setShipping] = useState(emptyAddress());
  const [sameAsBilling, setSameAsBilling] = useState(true);

  const applyPayload = useCallback((payload) => {
    setCheckout(payload);
    if (payload.billing) {
      setBilling({ ...emptyAddress(), ...payload.billing });
    }
    if (payload.shipping) {
      setShipping({ ...emptyAddress(), ...payload.shipping });
    }
    if (
      payload.customer &&
      payload.customer.email &&
      !(payload.billing && payload.billing.email)
    ) {
      setBilling((prev) => ({
        ...prev,
        email: payload.customer.email || prev.email,
        name: payload.customer.name || prev.name,
        phone: payload.customer.phone || prev.phone,
      }));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let payload;
      try {
        payload = await fetchCheckout();
      } catch (e) {
        payload = await createCheckout();
      }
      applyPayload(payload);
    } catch (e) {
      setError(e.message || "Unable to start checkout.");
      setCheckout(null);
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      const payload = await fn();
      applyPayload(payload);
      return payload;
    } catch (e) {
      setError(e.message || "Checkout update failed.");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const onSaveAddresses = async (e) => {
    e.preventDefault();
    if (!checkout || !checkout.checkout_id) return;
    const checkoutId = checkout && checkout.checkout_id;
    await run(() => updateCheckoutBilling(checkoutId, billing));
    const ship = sameAsBilling ? billing : shipping;
    await run(() => updateCheckoutShipping(checkoutId, ship));
    await run(() => validateCheckout(checkoutId));
  };

  const onPlaceOrder = async () => {
    if (!checkout || !checkout.checkout_id) return;
    setBusy(true);
    setError("");
    try {
      await updateCheckoutBilling(checkout.checkout_id, billing);
      await updateCheckoutShipping(
        checkout.checkout_id,
        sameAsBilling ? billing : shipping
      );
      await validateCheckout(checkout.checkout_id);
      const orderPayload = await createOrder(checkout.checkout_id);
      const order = orderPayload && orderPayload.order;
      const number = order && order.number;
      const paymentReq = order && order.payment_request;
      if (!number) {
        throw new Error("Order number missing from response.");
      }

      if (paymentReq && paymentReq.configured && paymentReq.adapter === "stripe") {
        if (!stripe) {
          throw new Error(
            "Stripe.js has not loaded yet. Refresh and try again."
          );
        }
        const tokenResult = await stripe.createToken({
          name: billing.name || undefined,
        });
        if (tokenResult.error) {
          throw new Error(
            tokenResult.error.message || "Card tokenization failed."
          );
        }
        const cardToken = tokenResult.token && tokenResult.token.id;
        if (!cardToken) {
          throw new Error("Could not create card token.");
        }
        await chargeOrder(number, cardToken, `fe-${number}`);
      } else if (paymentReq && paymentReq.config_required) {
        throw new Error(paymentReq.config_required);
      }

      if (history) {
        history.push(`/order/${encodeURIComponent(number)}`);
      }
    } catch (e) {
      setError(e.message || "Could not complete checkout.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="commerce-checkout commerce-checkout--loading">
        <p>Preparing checkout…</p>
      </div>
    );
  }

  if (!checkout) {
    return (
      <div className="commerce-checkout commerce-checkout--empty">
        <h1>Checkout</h1>
        {error ? <p className="commerce-checkout__error">{error}</p> : null}
        <Link className="btn btn-danger" to="/cart">
          Back to cart
        </Link>
      </div>
    );
  }

  const cart = checkout.cart || {};
  const items = cart.items || [];
  const summary = checkout.summary || {};
  const warnings = checkout.warnings || [];
  const messages = checkout.messages || [];
  const payment = checkout.payment || {};

  return (
    <div className="commerce-checkout">
      <Helmet>
        <title>Checkout | Pentecost+</title>
      </Helmet>

      <div className="commerce-checkout__inner">
        <p className="commerce-checkout__crumb">
          <Link to="/shop">Shop</Link>
          <span aria-hidden="true"> / </span>
          <Link to="/cart">Cart</Link>
          <span aria-hidden="true"> / </span>
          <span>Checkout</span>
        </p>

        <h1 className="commerce-checkout__title">Checkout</h1>

        {error ? <p className="commerce-checkout__error">{error}</p> : null}
        {warnings.map((w, i) => (
          <p key={`w-${i}`} className="commerce-checkout__warning">
            {w.message || w.code}
          </p>
        ))}
        {messages.map((m, i) => (
          <p key={`m-${i}`} className="commerce-checkout__message">
            {typeof m === "string" ? m : m.message}
          </p>
        ))}

        {!items.length ? (
          <div className="commerce-checkout__empty">
            <p>Your cart is empty.</p>
            <Link className="btn btn-danger" to="/shop">
              Continue shopping
            </Link>
          </div>
        ) : (
          <div className="commerce-checkout__layout">
            <form className="commerce-checkout__form" onSubmit={onSaveAddresses}>
              <section>
                <h2>Billing</h2>
                <AddressFields
                  values={billing}
                  onChange={setBilling}
                  disabled={busy}
                  idPrefix="billing"
                />
              </section>

              <section>
                <h2>Shipping</h2>
                <label className="commerce-checkout__same">
                  <input
                    type="checkbox"
                    checked={sameAsBilling}
                    disabled={busy}
                    onChange={(e) => setSameAsBilling(e.target.checked)}
                  />{" "}
                  Same as billing
                </label>
                {!sameAsBilling ? (
                  <AddressFields
                    values={shipping}
                    onChange={setShipping}
                    disabled={busy}
                    idPrefix="shipping"
                  />
                ) : null}
              </section>

              <section>
                <h2>Payment</h2>
                <div className="commerce-checkout__card">
                  <CardElement
                    disabled={busy}
                    style={{ base: { fontSize: "16px" } }}
                  />
                </div>
              </section>

              <button type="submit" className="btn btn-secondary" disabled={busy}>
                Save &amp; validate
              </button>
            </form>

            <aside className="commerce-checkout__summary">
              <h2>Order summary</h2>
              <ul className="commerce-checkout__lines">
                {items.map((item) => (
                  <li key={item.id}>
                    <span>
                      {(item.product && item.product.name) || "Item"} ×{" "}
                      {item.quantity}
                    </span>
                    <span>{moneyLabel(item.pricing, "line_subtotal")}</span>
                  </li>
                ))}
              </ul>
              <dl>
                <div>
                  <dt>Subtotal</dt>
                  <dd>{moneyLabel(summary, "subtotal")}</dd>
                </div>
                <div>
                  <dt>Discounts</dt>
                  <dd>
                    {summary.discounts
                      ? `−${moneyLabel(summary, "discounts")}`
                      : moneyLabel(summary, "discounts")}
                  </dd>
                </div>
                <div>
                  <dt>Taxes</dt>
                  <dd>
                    {summary.taxes == null
                      ? "Calculated at checkout"
                      : moneyLabel(summary, "taxes")}
                  </dd>
                </div>
                <div>
                  <dt>Shipping</dt>
                  <dd>
                    {summary.shipping == null
                      ? "Digital — no shipping"
                      : moneyLabel(summary, "shipping")}
                  </dd>
                </div>
                <div className="commerce-checkout__total">
                  <dt>Total</dt>
                  <dd>{moneyLabel(summary, "total")}</dd>
                </div>
              </dl>

              <p className="commerce-checkout__payment-note">
                Payment methods:{" "}
                {payment.supported_methods && payment.supported_methods.length
                  ? payment.supported_methods.join(", ")
                  : payment.configured === false
                    ? "Provider not configured"
                    : "card"}
                .
              </p>

              <button
                type="button"
                className="btn btn-danger commerce-checkout__place"
                disabled={busy}
                onClick={onPlaceOrder}
              >
                {busy ? "Working…" : "Pay & place order"}
              </button>
              <p className="commerce-checkout__hint">
                Charges the server order total. Duplicate submissions are
                rejected safely.
              </p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function AddressFields({ values, onChange, disabled, idPrefix }) {
  const set = (key) => (e) => onChange({ ...values, [key]: e.target.value });
  const fields = [
    ["name", "Name"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["line1", "Address"],
    ["line2", "Address line 2"],
    ["city", "City"],
    ["region", "State / region"],
    ["postal_code", "Postal code"],
    ["country", "Country"],
  ];

  return (
    <div className="commerce-checkout__fields">
      {fields.map(([key, label]) => (
        <label key={key} htmlFor={`${idPrefix}-${key}`}>
          <span>{label}</span>
          <input
            id={`${idPrefix}-${key}`}
            type={key === "email" ? "email" : "text"}
            value={values[key] || ""}
            onChange={set(key)}
            disabled={disabled}
            autoComplete="off"
          />
        </label>
      ))}
    </div>
  );
}

export default injectStripe(CheckoutPage);
